import { ArrowLeftOutlined, EyeOutlined } from '@ant-design/icons';
import { PERMISSIONS, PRICING_MODES } from '@navicosa/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  TreeSelect,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, errorText } from '../../api/client';
import {
  type Brand,
  type Category,
  PRICING_MODE_LABEL,
  type ProductDetail,
  STATUS_LABEL,
} from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { categoryTree } from '../catalog/categoryTree';
import { ImagesTab } from './ImagesTab';
import { PreviewDrawer } from './PreviewDrawer';
import { PricesTab } from './PricesTab';
import { ServiceAreasTab } from './ServiceAreasTab';
import { SpecsTab } from './SpecsTab';

type InfoForm = Pick<
  ProductDetail,
  | 'category_id'
  | 'brand_id'
  | 'product_code'
  | 'name'
  | 'slug'
  | 'model'
  | 'pricing_mode'
  | 'show_public_price'
  | 'is_featured'
  | 'accepting_requests'
  | 'short_description'
  | 'description'
  | 'seo_title'
  | 'seo_description'
>;

export function ProductEditPage() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = useAuth();
  const canEdit = can(PERMISSIONS.CATALOG_MANAGE);
  const [form] = Form.useForm<InfoForm>();
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const cats = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/admin/categories'),
  });
  const brands = useQuery({
    queryKey: ['brands'],
    queryFn: () => api.get<Brand[]>('/admin/brands'),
  });
  const product = useQuery({
    queryKey: ['product', id],
    queryFn: () => api.get<ProductDetail>(`/admin/products/${id}`),
    enabled: !isNew,
  });
  const check = useQuery({
    queryKey: ['publish-check', id],
    queryFn: () =>
      api.get<{ can_publish: boolean; problems: string[] }>(`/admin/products/${id}/publish-check`),
    enabled: !isNew,
  });

  useEffect(() => {
    if (product.data) form.setFieldsValue(product.data);
  }, [product.data, form]);

  const refresh = (p?: ProductDetail) => {
    if (p) qc.setQueryData(['product', id], p);
    void qc.invalidateQueries({ queryKey: ['publish-check', id] });
    void qc.invalidateQueries({ queryKey: ['products'] });
  };

  const save = async (values: InfoForm) => {
    setSaving(true);
    try {
      if (isNew) {
        const created = await api.post<ProductDetail>('/admin/products', values);
        message.success('Đã tạo sản phẩm (nháp). Tiếp tục nhập thông số, ảnh, khu vực và giá.');
        nav(`/products/${created.id}`, { replace: true });
      } else {
        refresh(await api.patch<ProductDetail>(`/admin/products/${id}`, values));
        message.success('Đã lưu');
      }
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const action = async (path: string, ok: string) => {
    try {
      refresh(await api.post<ProductDetail>(`/admin/products/${id}/${path}`));
      message.success(ok);
    } catch (e) {
      message.error(errorText(e));
    }
  };

  if (!isNew && product.isLoading) return <Spin />;
  const p = product.data;

  const info = (
    <Form
      form={form}
      layout="vertical"
      onFinish={save}
      disabled={!canEdit}
      initialValues={{
        pricing_mode: 'hybrid',
        show_public_price: true,
        accepting_requests: true,
        is_featured: false,
      }}
    >
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="category_id"
            label="Danh mục"
            rules={[{ required: true, message: 'Chọn danh mục' }]}
          >
            <TreeSelect
              treeData={categoryTree(cats.data ?? [])}
              treeDefaultExpandAll
              placeholder="Chọn danh mục"
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="brand_id"
            label="Thương hiệu"
            extra="Xe cắt kéo định giá theo chiều cao – có thể để trống (D-08)"
          >
            <Select
              allowClear
              options={(brands.data ?? []).map((b) => ({ value: b.id, label: b.name }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={16}>
          <Form.Item name="name" label="Tên sản phẩm" rules={[{ required: true, min: 2 }]}>
            <Input placeholder="Xe nâng người cắt kéo điện 12m" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="product_code"
            label="Mã sản phẩm"
            rules={[
              { required: true, pattern: /^[A-Za-z0-9._-]{2,80}$/, message: 'Chữ, số, . _ -' },
            ]}
          >
            <Input placeholder="SL-12M" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="model" label="Model">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={16}>
          <Form.Item
            name="slug"
            label="Đường dẫn (slug)"
            extra="Để trống khi tạo mới – hệ thống tự sinh từ tên, không dấu"
          >
            <Input placeholder="xe-nang-nguoi-cat-keo-dien-12m" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="pricing_mode" label="Chế độ giá" rules={[{ required: true }]}>
            <Select
              options={PRICING_MODES.map((m) => ({ value: m, label: PRICING_MODE_LABEL[m] }))}
            />
          </Form.Item>
        </Col>
        <Col xs={8} md={4}>
          <Form.Item name="show_public_price" label="Công khai giá" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col xs={8} md={4}>
          <Form.Item name="is_featured" label="Nổi bật" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col xs={8} md={4}>
          <Form.Item
            name="accepting_requests"
            label="Nhận yêu cầu"
            valuePropName="checked"
            tooltip="Tắt để hiện nhãn 'Tạm ngừng nhận yêu cầu'"
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="short_description" label="Mô tả ngắn" rules={[{ max: 500 }]}>
            <Input.TextArea rows={2} showCount maxLength={500} />
          </Form.Item>
          <Form.Item name="description" label="Mô tả chi tiết / công việc phù hợp">
            <Input.TextArea rows={6} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="seo_title" label="SEO title">
            <Input maxLength={255} />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="seo_description" label="SEO description">
            <Input.TextArea rows={2} maxLength={1000} />
          </Form.Item>
        </Col>
      </Row>
      {canEdit && (
        <Button type="primary" htmlType="submit" loading={saving}>
          {isNew ? 'Tạo sản phẩm' : 'Lưu thông tin'}
        </Button>
      )}
    </Form>
  );

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav(-1)}>
          Quay lại
        </Button>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {isNew ? 'Thêm sản phẩm' : p?.name}
        </Typography.Title>
        {p && (
          <Tag
            color={
              p.status === 'published' ? 'green' : p.status === 'archived' ? 'orange' : 'default'
            }
          >
            {STATUS_LABEL[p.status]}
          </Tag>
        )}
        {p?.is_demo && <Tag color="purple">DEMO</Tag>}
      </Space>

      {p && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap>
            {canEdit && p.status !== 'published' && (
              <Button
                type="primary"
                disabled={!check.data?.can_publish}
                onClick={() => void action('publish', 'Đã xuất bản')}
              >
                Xuất bản
              </Button>
            )}
            {canEdit && p.status === 'published' && (
              <Button onClick={() => void action('unpublish', 'Đã chuyển về nháp')}>
                Ẩn (về nháp)
              </Button>
            )}
            {canEdit && p.status !== 'archived' && (
              <Popconfirm
                title="Lưu trữ sản phẩm? Khách sẽ không thấy sản phẩm này."
                onConfirm={() => void action('archive', 'Đã lưu trữ')}
              >
                <Button danger>Lưu trữ</Button>
              </Popconfirm>
            )}
            <Button icon={<EyeOutlined />} onClick={() => setPreview(true)}>
              Xem trước như khách
            </Button>
          </Space>
          {check.data && !check.data.can_publish && p.status !== 'published' && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message="Chưa đủ điều kiện xuất bản"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {check.data.problems.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              }
            />
          )}
        </Card>
      )}

      <Card>
        {isNew
          ? info
          : p && (
              <Tabs
                items={[
                  { key: 'info', label: 'Thông tin', children: info },
                  {
                    key: 'specs',
                    label: 'Thông số kỹ thuật',
                    children: <SpecsTab product={p} canEdit={canEdit} onSaved={refresh} />,
                  },
                  {
                    key: 'images',
                    label: `Ảnh (${p.images.length})`,
                    children: <ImagesTab product={p} canEdit={canEdit} onSaved={refresh} />,
                  },
                  {
                    key: 'areas',
                    label: 'Khu vực phục vụ',
                    children: <ServiceAreasTab product={p} canEdit={canEdit} onSaved={refresh} />,
                  },
                  ...(can(PERMISSIONS.PRICES_READ)
                    ? [
                        {
                          key: 'prices',
                          label: 'Giá thuê',
                          children: <PricesTab productId={p.id} onChanged={() => refresh()} />,
                        },
                      ]
                    : []),
                ]}
              />
            )}
      </Card>
      {p && <PreviewDrawer productId={p.id} open={preview} onClose={() => setPreview(false)} />}
    </>
  );
}
